import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
    Container, Header, Segment, Button, Input, Label, Message, Loader, Icon, Table, Divider,
} from 'semantic-ui-react';
import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PATHS } from '../../../paths';

/**
 * The SKU curriculum editor.
 *
 * Two halves, and the second is the one that earns the screen:
 *
 *   **What is allowed** — the stream / program / class label tree, editable.
 *   **What is in use**  — every combination the organisation's classes actually contain, with
 *                         the ones no rule covers listed first.
 *
 * An editor showing only the rules is a form somebody fills in once, wrongly, and never looks at
 * again. The comparison is what makes it maintainable: on production there are 98 classes using
 * thirteen combinations the supplied spreadsheet never mentioned, including 41 under ED-AR.
 * Those are either curriculum the sheet forgot or genuine mistakes, and only somebody who knows
 * the programmes can say which — so the screen puts the question in front of them with the
 * counts attached, rather than silently choosing.
 */

const GET_SKU_RULES = gql`
  query SkuRulesPage {
    canConfigureSkuRules
    skuRules { id stream program classLabel label classCount }
    skuCodesInUse { stream program classLabel classCount allowed example }
  }
`;

const ADD_RULE = gql`
  mutation AddSkuRule($stream: String!, $program: String, $classLabel: String, $label: String) {
    addSkuRule(stream: $stream, program: $program, classLabel: $classLabel, label: $label)
  }
`;

const REMOVE_RULE = gql`
  mutation RemoveSkuRule($id: Int!) { removeSkuRule(id: $id) }
`;

const ADD_MISSING = gql`
  mutation AddMissingSkuRules { addMissingSkuRules }
`;

interface Rule {
    id: number;
    stream: string;
    program: string | null;
    classLabel: string | null;
    label: string | null;
    classCount: number;
}

interface ProgramNode { row: Rule | null; labels: Rule[] }
interface StreamNode { row: Rule | null; programs: Map<string, ProgramNode> }

/** Rules are a flat list of rows; the screen shows them as the tree they describe. */
const asTree = (rules: Rule[]) => {
    const streams = new Map<string, StreamNode>();

    for (const r of rules) {
        const stream = streams.get(r.stream)
            ?? streams.set(r.stream, { row: null, programs: new Map() }).get(r.stream)!;

        if (r.program == null) { stream.row = r; continue; }

        const program = stream.programs.get(r.program)
            ?? stream.programs.set(r.program, { row: null, labels: [] }).get(r.program)!;

        if (r.classLabel == null) { program.row = r; continue; }
        program.labels.push(r);
    }

    return streams;
};

const SkuRulesView: React.FC = () => {
    const { data, loading, error, refetch } = useQuery(GET_SKU_RULES, { fetchPolicy: 'network-only' });
    const [addRule, { loading: adding }] = useMutation(ADD_RULE);
    const [removeRule] = useMutation(REMOVE_RULE);
    const [addMissing, { loading: addingMissing }] = useMutation(ADD_MISSING);

    const [draft, setDraft] = useState({ stream: '', program: '', classLabel: '', label: '' });
    const [actionError, setActionError] = useState<string | null>(null);

    const act = async (fn: () => Promise<any>) => {
        setActionError(null);
        try {
            await fn();
            await refetch();
        } catch (e: any) {
            setActionError(e?.message ?? 'That did not work.');
        }
    };

    if (loading) {
        return (
            <ResponsiveContainer>
                <Loader active inline="centered" style={{ marginTop: '5em' }}>Loading SKU rules...</Loader>
            </ResponsiveContainer>
        );
    }

    if (error) {
        return (
            <ResponsiveContainer>
                <Container text><Message error content="Could not load the SKU rules." /></Container>
            </ResponsiveContainer>
        );
    }

    // Server-gated too; this only avoids showing a page that would redirect.
    if (!data?.canConfigureSkuRules) {
        return <Navigate to={PATHS.ADMIN_SETTINGS} replace />;
    }

    const rules: Rule[] = data.skuRules ?? [];
    const inUse = data.skuCodesInUse ?? [];
    const gaps = inUse.filter((c: any) => !c.allowed);
    const tree = asTree(rules);

    return (
        <ResponsiveContainer>
            <Container style={{ paddingTop: '2em', paddingBottom: '4em' }}>
                <Header as="h1">Class code rules</Header>
                <p style={{ color: '#767676', maxWidth: '46em' }}>
                    A class code looks like <code>KP-ED-LG-EN-L2-C4-NOV2024</code>. These rules decide
                    which <strong>stream</strong>, <strong>program</strong> and <strong>class label</strong>{' '}
                    combinations are allowed — parts two, three and four. Creating a class with a
                    combination that is not listed is refused, and the Classes page can list the ones
                    that already exist.
                </p>

                {rules.length === 0 && (
                    <Message info>
                        <Message.Header>Nothing is configured yet</Message.Header>
                        <p>
                            While this is empty, class codes are not checked against any curriculum —
                            only their shape. Add the combinations you use, or start from what your
                            classes already contain.
                        </p>
                    </Message>
                )}

                {actionError && <Message error content={actionError} />}

                {/* --- the gaps, first, because they are the work --- */}
                {gaps.length > 0 && (
                    <Segment color="orange">
                        <Header as="h3" style={{ marginBottom: '0.2em' }}>
                            Codes in use that no rule allows
                        </Header>
                        <p style={{ color: '#767676' }}>
                            These classes already exist. Either the combination is legitimate and belongs
                            in the rules, or the class codes are wrong and want fixing — the counts are
                            there to help tell which.
                        </p>

                        <Table compact size="small">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Combination</Table.HeaderCell>
                                    <Table.HeaderCell>Classes</Table.HeaderCell>
                                    <Table.HeaderCell>Example</Table.HeaderCell>
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {gaps.map((g: any) => (
                                    <Table.Row key={`${g.stream}-${g.program}-${g.classLabel}`}>
                                        <Table.Cell>
                                            <code>{g.stream}-{g.program}-{g.classLabel}</code>
                                        </Table.Cell>
                                        <Table.Cell>{g.classCount}</Table.Cell>
                                        <Table.Cell><code style={{ fontSize: '0.85em' }}>{g.example}</code></Table.Cell>
                                        <Table.Cell textAlign="right">
                                            <Button
                                                size="mini"
                                                basic
                                                onClick={() => act(() => addRule({
                                                    variables: {
                                                        stream: g.stream,
                                                        program: g.program,
                                                        classLabel: g.classLabel,
                                                    },
                                                }))}
                                            >
                                                Allow this
                                            </Button>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>

                        <Button
                            basic
                            color="orange"
                            loading={addingMissing}
                            disabled={addingMissing}
                            onClick={() => act(() => addMissing())}
                        >
                            Allow all {gaps.length} of them
                        </Button>
                        <span style={{ marginLeft: '1em', color: '#767676', fontSize: '0.9em' }}>
                            Treats what is already in the data as correct. Sensible when adopting rules
                            over existing classes; not when you are trying to find mistakes.
                        </span>
                    </Segment>
                )}

                {/* --- the rules themselves --- */}
                <Header as="h2" style={{ marginTop: '1.5em' }}>Allowed combinations</Header>

                {Array.from(tree.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([stream, { row, programs }]) => (
                    <Segment key={stream}>
                        <Header as="h3" style={{ marginBottom: '0.4em' }}>
                            <code>{stream}</code>
                            {row?.label && <span style={{ color: '#767676', fontWeight: 'normal', marginLeft: '0.6em', fontSize: '0.8em' }}>{row.label}</span>}
                            <Label size="mini" style={{ marginLeft: '0.6em' }}>{row?.classCount ?? 0} classes</Label>
                            {row && (
                                <Button
                                    size="mini"
                                    basic
                                    color="red"
                                    floated="right"
                                    onClick={() => act(() => removeRule({ variables: { id: row.id } }))}
                                >
                                    Remove stream
                                </Button>
                            )}
                        </Header>

                        {programs.size === 0 && (
                            <p style={{ color: '#767676', fontSize: '0.9em' }}>
                                No programs listed, so any program and label is accepted in this stream.
                            </p>
                        )}

                        {Array.from(programs.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([program, { row: pRow, labels }]) => (
                            <div key={program} style={{ marginBottom: '0.8em', paddingLeft: '1em', borderLeft: '3px solid #e8e8e8' }}>
                                <div>
                                    <code style={{ fontWeight: 600 }}>{stream}-{program}</code>
                                    {pRow?.label && <span style={{ color: '#767676', marginLeft: '0.6em', fontSize: '0.85em' }}>{pRow.label}</span>}
                                    <Label size="mini" style={{ marginLeft: '0.6em' }}>{pRow?.classCount ?? 0}</Label>
                                    {pRow && (
                                        <Icon
                                            name="trash alternate outline"
                                            link
                                            style={{ marginLeft: '0.6em', color: '#db2828' }}
                                            title={`Remove ${stream}-${program} and its labels`}
                                            onClick={() => act(() => removeRule({ variables: { id: pRow.id } }))}
                                        />
                                    )}
                                </div>

                                <div style={{ marginTop: '4px' }}>
                                    {labels.length === 0 ? (
                                        <span style={{ color: '#767676', fontSize: '0.85em' }}>
                                            No labels listed — any label accepted here.
                                        </span>
                                    ) : [...labels].sort((a: Rule, b: Rule) => (a.classLabel ?? '').localeCompare(b.classLabel ?? '')).map((l: Rule) => (
                                        <Label key={l.id} basic style={{ marginRight: '4px', marginBottom: '4px' }}>
                                            {l.classLabel}
                                            {l.label && <span style={{ color: '#767676', fontWeight: 'normal' }}> · {l.label}</span>}
                                            <Label.Detail>{l.classCount}</Label.Detail>
                                            <Icon
                                                name="delete"
                                                onClick={() => act(() => removeRule({ variables: { id: l.id } }))}
                                            />
                                        </Label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </Segment>
                ))}

                <Divider />

                {/* --- add --- */}
                <Header as="h3">Add a combination</Header>
                <p style={{ color: '#767676', fontSize: '0.9em', maxWidth: '44em' }}>
                    A stream on its own accepts any program under it. Add a program to narrow it, and a
                    label to narrow that. Leaving the later boxes empty is how you say "anything".
                </p>
                <div style={{ display: 'flex', gap: '0.5em', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Input
                        placeholder="Stream (ED)"
                        value={draft.stream}
                        style={{ width: '10em' }}
                        onChange={(_, d) => setDraft((p) => ({ ...p, stream: d.value }))}
                    />
                    <Input
                        placeholder="Program (LG)"
                        value={draft.program}
                        style={{ width: '10em' }}
                        onChange={(_, d) => setDraft((p) => ({ ...p, program: d.value }))}
                    />
                    <Input
                        placeholder="Label (EN)"
                        value={draft.classLabel}
                        style={{ width: '10em' }}
                        onChange={(_, d) => setDraft((p) => ({ ...p, classLabel: d.value }))}
                    />
                    <Input
                        placeholder="What it means (English)"
                        value={draft.label}
                        style={{ width: '16em' }}
                        onChange={(_, d) => setDraft((p) => ({ ...p, label: d.value }))}
                    />
                    <Button
                        primary
                        loading={adding}
                        disabled={adding || draft.stream.trim().length === 0}
                        onClick={async () => {
                            await act(() => addRule({
                                variables: {
                                    stream: draft.stream,
                                    program: draft.program || null,
                                    classLabel: draft.classLabel || null,
                                    label: draft.label || null,
                                },
                            }));
                            setDraft({ stream: '', program: '', classLabel: '', label: '' });
                        }}
                    >
                        Add
                    </Button>
                </div>
            </Container>
        </ResponsiveContainer>
    );
};

export { SkuRulesView };
